// src/components/AdquisicionBienes.jsx
import React, { useMemo, useState, useEffect } from "react";
import logoAlcaldia from "../assets/logooficial.png";
import { apiConfig } from "../config/api.config";

export default function AdquisicionBienes({ contratoFirmado, onClose }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mensaje, setMensaje] = useState("");

  // Campos obligatorios (solicitante)
  const [unspsc, setUnspsc] = useState("");
  const [descripcionCodigo, setDescripcionCodigo] = useState("");
  const [correoSolicitante, setCorreoSolicitante] = useState("");

  const CONTACTO_FIJO = "6042740069";

  const objetoContractual =
    contratoFirmado?.objetoContractual ||
    contratoFirmado?.objeto_contractual ||
    contratoFirmado?.objeto ||
    "";

  const radicado =
    contratoFirmado?.radicado ||
    contratoFirmado?.radicado_contrato ||
    contratoFirmado?.numero_contrato ||
    "";

  const secretaria =
    contratoFirmado?.secretaria ||
    contratoFirmado?.dependencia ||
    contratoFirmado?.nombre_dependencia ||
    "";

  // Si el contrato trae un correo, lo prellenamos (pero el usuario puede corregirlo)
  useEffect(() => {
    const correo =
      contratoFirmado?.correo ||
      contratoFirmado?.correo_solicitante ||
      contratoFirmado?.email ||
      contratoFirmado?.email_solicitante ||
      "";
    if (correo && !correoSolicitante) setCorreoSolicitante(String(correo));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contratoFirmado]);

  // URLs
  const urls = useMemo(() => {
    const epCreate =
      apiConfig.endpoints?.almacenSolicitudesCreate ||
      "/api/almacen/solicitudes/create/index.php";

    return {
      create: apiConfig.getUrl(epCreate),
    };
  }, []);

  const safeJson = async (resp) => {
    const text = await resp.text().catch(() => "");
    try {
      return text ? JSON.parse(text) : {};
    } catch {
      return { raw: text };
    }
  };

  const normalize = (v) => String(v ?? "").trim();

  const validate = () => {
    const u = normalize(unspsc);
    const d = normalize(descripcionCodigo);
    const c = normalize(correoSolicitante);

    if (!u) return "Debe ingresar el CÓDIGO UNSPSC.";
    if (!d) return "Debe ingresar la DESCRIPCIÓN DEL CÓDIGO.";
    if (!c) return "Debe ingresar el CORREO ELECTRÓNICO DE LA DEPENDENCIA.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c)) {
      return "El CORREO ELECTRÓNICO no tiene un formato válido.";
    }
    return "";
  };

  const handleEnviarSolicitud = async () => {
    try {
      if (loading) return;

      setError("");
      setMensaje("");

      const msg = validate();
      if (msg) {
        setError(msg);
        return;
      }

      setLoading(true);

      const contractId = String(contratoFirmado?.id || "")
        .replace(/[{}]/g, "")
        .trim();

      if (!contractId) {
        setError("No se encontró el ID del contrato.");
        return;
      }

      // ✅ Todo lo que llena el solicitante se guarda en form_data
      const form_data = {
        unspsc: normalize(unspsc),
        // nombre solicitado por ti: “descripcion del codigo”
        descripcion_codigo: normalize(descripcionCodigo),
        correo: normalize(correoSolicitante),
        contacto: CONTACTO_FIJO,
      };

      // ✅ Payload compatible: manda form_data + duplicados por si el backend los lee planos
      // También manda contractId por compatibilidad
      const payload = {
        contract_id: contractId,
        contractId: contractId,
        status: "pending",
        notes: `Solicitud de certificado (adquisición) para el contrato ${radicado || ""}`,

        form_data,

        // compatibilidad (si el backend toma campos planos)
        unspsc: form_data.unspsc,
        descripcion_codigo: form_data.descripcion_codigo,
        correo: form_data.correo,
        contacto: form_data.contacto,
      };

      const resp = await fetch(urls.create, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await safeJson(resp);

      if (!resp.ok) {
        const msgErr =
          data?.error ||
          data?.message ||
          data?.detail ||
          data?.raw ||
          `Error HTTP ${resp.status}`;
        throw new Error(msgErr);
      }

      setMensaje("Solicitud enviada correctamente al área de almacén.");
      alert("Solicitud enviada correctamente al área de almacén.");
      onClose && onClose();
    } catch (err) {
      console.error("Error al enviar solicitud:", err);
      setError("Error al enviar la solicitud: " + (err?.message || "Error desconocido"));
    } finally {
      setLoading(false);
    }
  };

  const titulo = "Solicitud de Certificado de Adquisición";
  const subtitulo = `Contrato: ${radicado || "-"} • Secretaría: ${secretaria || "-"}`;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "linear-gradient(to bottom, rgba(30,58,138,0.9), rgba(30,64,175,0.9))",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: "16px",
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          width: "100%",
          maxWidth: "900px",
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
          display: "flex",
          flexDirection: "column",
          maxHeight: "95vh",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "16px",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <div
              style={{
                width: 64,
                height: 64,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <img
                src={logoAlcaldia}
                alt="Alcaldía"
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </div>

            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: "#111827" }}>{titulo}</div>
              <div style={{ fontSize: 12.5, color: "#6b7280" }}>{subtitulo}</div>
            </div>
          </div>

          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              fontSize: "26px",
              cursor: loading ? "not-allowed" : "pointer",
              color: "#6b7280",
              lineHeight: 1,
            }}
            aria-label="Cerrar"
            title="Cerrar"
            disabled={loading}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "16px 24px", overflowY: "auto" }}>
          {error && (
            <div
              style={{
                marginBottom: "12px",
                padding: "10px 12px",
                backgroundColor: "#fef2f2",
                color: "#b91c1c",
                borderRadius: "8px",
                border: "1px solid #fecaca",
                fontWeight: 700,
              }}
            >
              {error}
            </div>
          )}

          {mensaje && (
            <div
              style={{
                marginBottom: "12px",
                padding: "10px 12px",
                backgroundColor: "#ecfdf5",
                color: "#15803d",
                borderRadius: "8px",
                border: "1px solid #bbf7d0",
                fontWeight: 700,
              }}
            >
              {mensaje}
            </div>
          )}

          <div style={{ marginBottom: 12, color: "#6b7280", fontSize: 13 }}>
            Los campos marcados con <strong>*</strong> son obligatorios.
          </div>

          {/* Objeto contractual (solo lectura) */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontWeight: 800, marginBottom: 6, color: "#374151" }}>
              Objeto contractual (solo lectura)
            </div>

            <textarea
              value={objetoContractual}
              readOnly
              rows={6}
              style={{
                width: "100%",
                border: "1px solid #d1d5db",
                borderRadius: 8,
                padding: 10,
                fontSize: 13.5,
                backgroundColor: "#f9fafb",
                resize: "vertical",
              }}
            />
          </div>

          {/* Campos rojos (solicitante) */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={{ fontWeight: 800, fontSize: 13, color: "#111827" }}>
                Código UNSPSC *
              </label>
              <input
                value={unspsc}
                onChange={(e) => setUnspsc(e.target.value)}
                placeholder="Ej: 80111600"
                disabled={loading}
                style={{
                  width: "100%",
                  border: "1px solid #d1d5db",
                  borderRadius: 8,
                  padding: "10px 10px",
                  fontSize: 13,
                  outline: "none",
                }}
              />
            </div>

            <div>
              <label style={{ fontWeight: 800, fontSize: 13, color: "#111827" }}>
                Correo electrónico del solicitante *
              </label>
              <input
                value={correoSolicitante}
                onChange={(e) => setCorreoSolicitante(e.target.value)}
                type="email"
                placeholder="correo@dominio.com"
                disabled={loading}
                style={{
                  width: "100%",
                  border: "1px solid #d1d5db",
                  borderRadius: 8,
                  padding: "10px 10px",
                  fontSize: 13,
                  outline: "none",
                }}
              />
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <label style={{ fontWeight: 800, fontSize: 13, color: "#111827" }}>
              Descripción del código *
            </label>
            <textarea
              value={descripcionCodigo}
              onChange={(e) => setDescripcionCodigo(e.target.value)}
              rows={4}
              placeholder="Describe el bien/servicio asociado al UNSPSC"
              disabled={loading}
              style={{
                width: "100%",
                border: "1px solid #d1d5db",
                borderRadius: 8,
                padding: 10,
                fontSize: 13,
                resize: "vertical",
                outline: "none",
              }}
            />
          </div>

          <div style={{ marginTop: 12 }}>
            <label style={{ fontWeight: 800, fontSize: 13, color: "#111827" }}>
              Contacto (fijo)
            </label>
            <input
              value={CONTACTO_FIJO}
              readOnly
              style={{
                width: "100%",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                padding: "10px 10px",
                fontSize: 13,
                backgroundColor: "#f3f4f6",
              }}
            />
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "12px 24px",
            borderTop: "1px solid #e5e7eb",
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
          }}
        >
          <button
            onClick={onClose}
            disabled={loading}
            style={{
              padding: "10px 14px",
              borderRadius: 8,
              border: "1px solid #d1d5db",
              background: "#f3f4f6",
              cursor: loading ? "not-allowed" : "pointer",
              fontWeight: 800,
            }}
          >
            Cancelar
          </button>

          <button
            onClick={handleEnviarSolicitud}
            disabled={loading}
            style={{
              padding: "10px 14px",
              borderRadius: 8,
              border: "none",
              background: "#16a34a",
              color: "white",
              fontWeight: 900,
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            {loading ? "Enviando..." : "Enviar solicitud"}
          </button>
        </div>
      </div>
    </div>
  );
}
